import { computed } from 'vue'

// SEO best practices limits
const SEO_LIMITS = {
  title: {
    max: 60,
    min: 30,
    warning: 'Title should be between 30-60 characters for optimal SEO'
  },
  description: {
    max: 160,
    min: 120,
    warning: 'Description should be between 120-160 characters for optimal SEO'
  },
  ogTitle: {
    max: 60,
    min: 30,
    warning: 'Open Graph title should be between 30-60 characters'
  },
  ogDescription: {
    max: 160,
    min: 120,
    warning: 'Open Graph description should be between 120-160 characters'
  }
}

// Helper function to validate image URL
function validateImageUrl(url) {
  if (!url) {
    return {
      isValid: false,
      warning: 'Image URL is missing'
    }
  }

  try {
    const urlObj = new URL(url)
    const isValidDomain = urlObj.hostname.includes('turnkey')
    
    return {
      isValid: isValidDomain,
      warning: isValidDomain ? null : 'Image URL should be from the turnkey domain'
    }
  } catch (e) {
    return {
      isValid: false,
      warning: 'Invalid image URL format'
    }
  }
}

export function useSeoValidation(content) {
  const headContent = content.head || {}

  const validationResults = computed(() => {
    const results = {}
    
    // Check title
    if (headContent.title) {
      const length = headContent.title.length
      results.title = {
        length,
        isValid: length >= SEO_LIMITS.title.min && length <= SEO_LIMITS.title.max,
        warning: length < SEO_LIMITS.title.min || length > SEO_LIMITS.title.max 
          ? SEO_LIMITS.title.warning 
          : null
      }
    }

    // Check description
    if (headContent.description) {
      const length = headContent.description.length
      results.description = {
        length,
        isValid: length >= SEO_LIMITS.description.min && length <= SEO_LIMITS.description.max,
        warning: length < SEO_LIMITS.description.min || length > SEO_LIMITS.description.max 
          ? SEO_LIMITS.description.warning 
          : null
      }
    }

    // Check meta tags
    if (headContent.meta) {
      headContent.meta.forEach((meta, index) => {
        if (meta.name === 'og:title') {
          const length = meta.content.length
          results.ogTitle = {
            length,
            isValid: length >= SEO_LIMITS.ogTitle.min && length <= SEO_LIMITS.ogTitle.max,
            warning: length < SEO_LIMITS.ogTitle.min || length > SEO_LIMITS.ogTitle.max 
              ? SEO_LIMITS.ogTitle.warning 
              : null
          }
        }
        if (meta.name === 'og:description') {
          const length = meta.content.length
          results.ogDescription = {
            length,
            isValid: length >= SEO_LIMITS.ogDescription.min && length <= SEO_LIMITS.ogDescription.max,
            warning: length < SEO_LIMITS.ogDescription.min || length > SEO_LIMITS.ogDescription.max 
              ? SEO_LIMITS.ogDescription.warning 
              : null
          }
        }
        if (meta.name === 'og:image') {
          const imageValidation = validateImageUrl(meta.content)
          results.ogImage = {
            url: meta.content,
            isValid: imageValidation.isValid,
            warning: imageValidation.warning
          }
        }
      })
    }

    return results
  })

  return {
    validationResults,
    hasWarnings: computed(() => 
      Object.values(validationResults.value).some(result => result?.warning)
    ),
    getWarnings: computed(() => 
      Object.entries(validationResults.value)
        .filter(([_, result]) => result?.warning)
        .map(([key, result]) => ({
          field: key,
          message: result.warning,
          length: result.length,
          url: result.url
        }))
    )
  }
} 